import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'cuponeo_favoritos';

function readFavs() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

/**
 * Hook para gestionar favoritos del usuario.
 * Persiste en localStorage — listo para integración con API cuando el usuario esté autenticado.
 * Usa useState + evento personalizado en lugar de useSyncExternalStore para evitar bucles infinitos.
 */
export function useFavorites() {
  const [favorites, setFavorites] = useState(readFavs);

  // Sincronizar entre pestañas y entre componentes
  useEffect(() => {
    function sync() {
      setFavorites(readFavs());
    }
    window.addEventListener('storage', sync);
    window.addEventListener('favorites-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('favorites-changed', sync);
    };
  }, []);

  const isFavorite = useCallback((eventId) => {
    return favorites.includes(Number(eventId));
  }, [favorites]);

  const toggleFavorite = useCallback((eventId) => {
    const id = Number(eventId);
    const current = readFavs();
    const next = current.includes(id)
      ? current.filter(x => x !== id)
      : [...current, id];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setFavorites(next);
    window.dispatchEvent(new Event('favorites-changed'));
  }, []);

  const removeFavorite = useCallback((eventId) => {
    const id = Number(eventId);
    const current = readFavs();
    const next = current.filter(x => x !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setFavorites(next);
    window.dispatchEvent(new Event('favorites-changed'));
  }, []);

  const count = favorites.length;

  return { favorites, isFavorite, toggleFavorite, removeFavorite, count };
}
